import { Quote, Trophy } from "lucide-react";

function formatDollar(n) {
  return "$" + Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

/**
 * EventHighlights — renders the photo gallery, total raised, and testimonials
 * for a past event. Only shown when the event has any highlights data.
 */
export default function EventHighlights({ event }) {
  const highlights = Array.isArray(event.highlights) ? event.highlights.filter((h) => h && h.image_url) : [];
  const testimonials = Array.isArray(event.testimonials) ? event.testimonials.filter((t) => t && t.quote) : [];
  const raised = event.total_raised;

  if (!raised && highlights.length === 0 && testimonials.length === 0) return null;

  return (
    <div className="mt-10 pt-10 border-t border-bdu-gold/15" data-testid={`event-highlights-${event.id}`}>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Trophy size={14} /> Past Ball Highlights
          </div>
          <h3 className="mt-3 font-serif text-3xl text-bdu-cream">A night the community built together.</h3>
        </div>
        {typeof raised === "number" && raised > 0 && (
          <div
            className="border border-bdu-gold/40 px-5 py-3 bg-bdu-gold/[0.04]"
            data-testid={`event-total-raised-${event.id}`}
          >
            <div className="label-eyebrow" style={{ fontSize: "0.55rem" }}>Total Raised</div>
            <div className="font-serif text-3xl text-bdu-gold mt-1">{formatDollar(raised)}</div>
          </div>
        )}
      </div>

      {highlights.length > 0 && (
        <div
          className="grid grid-cols-2 md:grid-cols-3 gap-3"
          data-testid={`event-gallery-${event.id}`}
        >
          {highlights.map((h, i) => (
            <figure
              key={i}
              className="relative overflow-hidden border border-bdu-gold/10 group"
              data-testid={`event-gallery-item-${event.id}-${i}`}
            >
              <img
                src={h.image_url}
                alt={h.caption || `Highlight ${i + 1}`}
                className="w-full h-48 md:h-56 object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              {h.caption && (
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-4 py-3 text-xs text-bdu-cream tracking-wide">
                  {h.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      )}

      {testimonials.length > 0 && (
        <div className="mt-10 grid md:grid-cols-2 gap-4" data-testid={`event-testimonials-${event.id}`}>
          {testimonials.map((t, i) => (
            <blockquote
              key={i}
              className="border-l-2 border-bdu-gold pl-6 py-3"
              data-testid={`event-testimonial-${event.id}-${i}`}
            >
              <Quote size={16} className="text-bdu-gold/60" />
              <p className="mt-2 font-serif italic text-lg text-bdu-cream/90 leading-relaxed">
                &ldquo;{t.quote}&rdquo;
              </p>
              <footer className="mt-3 text-xs uppercase tracking-[0.2em] text-bdu-cream/50">
                — {t.author || "Guest"}
                {t.role ? <span className="text-bdu-gold/70"> · {t.role}</span> : null}
              </footer>
            </blockquote>
          ))}
        </div>
      )}
    </div>
  );
}
